import React, { useEffect, useState } from 'react';
import { X, Edit2, Trash2, Package, Calendar, User, FileText, History } from 'lucide-react';
import type { Asset } from '@/types';
import { AssetHistoryTimeline, type TimelineEvent } from './AssetHistoryTimeline';
import { assetApi } from '@/utils/api';
import { assetHistoryEvents } from '@/utils/assetHistory';

interface AssetDetailModalProps {
  asset: Asset;
  onClose: () => void;
  onEdit: () => void;
  onDelete: () => void;
  /**
   * WD-T3 M3: capability gate passed by the caller (AssetsPage uses the same
   * tier gate it computes for Add Asset — worker tier is false). When false
   * the destructive Edit/Delete actions are OMITTED entirely, not merely
   * disabled: a worker viewing their assigned gear gets a read-only modal.
   * Defaults to true so every other caller keeps the current behavior.
   */
  canManage?: boolean;
}

export function AssetDetailModal({ asset, onClose, onEdit, onDelete, canManage = true }: AssetDetailModalProps) {
  const [activeTab, setActiveTab] = useState<'details' | 'history'>('details');

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'AVAILABLE':
        return 'bg-green-100 text-green-800';
      case 'IN_USE':
        return 'bg-blue-100 text-blue-800';
      case 'DAMAGED':
        return 'bg-red-100 text-red-800';
      case 'EXPIRED':
        return 'bg-gray-100 text-gray-800';
      case 'MAINTENANCE':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const [history, setHistory] = useState<TimelineEvent[]>([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [historyError, setHistoryError] = useState('');
  useEffect(() => {
    if (activeTab !== 'history') return undefined;
    let active = true;
    setHistoryLoading(true);
    setHistoryError('');
    setHistory([]);
    assetApi.getById(asset.id).then(response => {
      if (active) setHistory(assetHistoryEvents(response.data));
    }).catch(() => {
      if (active) setHistoryError('Unable to load saved asset history. Reopen History to retry.');
    }).finally(() => { if (active) setHistoryLoading(false); });
    return () => { active = false; };
  }, [asset.id, activeTab]);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <Package className="h-6 w-6 text-blue-600" />
            <h2 className="text-2xl font-bold text-gray-900">Asset Details</h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <div className="flex px-6">
            <button
              onClick={() => setActiveTab('details')}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition ${
                activeTab === 'details'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center gap-2">
                <FileText className="h-4 w-4" />
                Details
              </div>
            </button>
            <button
              onClick={() => setActiveTab('history')}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition ${
                activeTab === 'history'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center gap-2">
                <History className="h-4 w-4" />
                History
              </div>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {activeTab === 'details' ? (
            <>
          {/* Basic Info */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-gray-600">Asset Name</label>
                <p className="text-base font-medium text-gray-900">{asset.name}</p>
              </div>
              <div>
                <label className="text-sm text-gray-600">Type</label>
                <p className="text-base font-medium text-gray-900">{asset.type.replace(/_/g, ' ')}</p>
              </div>
              <div>
                <label className="text-sm text-gray-600">Serial Number</label>
                <p className="text-base font-medium text-gray-900">{asset.serialNumber || 'N/A'}</p>
              </div>
              <div>
                <label className="text-sm text-gray-600">Status</label>
                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(asset.status)}`}>
                  {asset.status.replace(/_/g, ' ')}
                </span>
              </div>
              {asset.manufacturer && (
                <div>
                  <label className="text-sm text-gray-600">Manufacturer</label>
                  <p className="text-base font-medium text-gray-900">{asset.manufacturer}</p>
                </div>
              )}
              {asset.model && (
                <div>
                  <label className="text-sm text-gray-600">Model</label>
                  <p className="text-base font-medium text-gray-900">{asset.model}</p>
                </div>
              )}
            </div>
          </div>

          {/* Location & Purchase Info */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Location & Details</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-gray-600">Office / Market</label>
                <p className="text-base font-medium text-gray-900">
                  {asset.market ? asset.market.name : <span className="text-gray-400">Not assigned</span>}
                </p>
              </div>
              <div>
                <label className="text-sm text-gray-600">Sub-Location</label>
                <p className="text-base font-medium text-gray-900">
                  {asset.location || <span className="text-gray-400">Not specified</span>}
                </p>
              </div>
              <div>
                <label className="text-sm text-gray-600">Purchase Price</label>
                <p className="text-base font-medium text-gray-900">
                  {asset.purchasePrice ? `$${asset.purchasePrice.toFixed(2)}` : <span className="text-gray-400">Not specified</span>}
                </p>
              </div>
              {(asset as any).inspectionInterval && (asset as any).inspectionInterval !== 'NONE' && (
                <div>
                  <label className="text-sm text-gray-600">Inspection Interval</label>
                  <p className="text-base font-medium text-gray-900">
                    {(asset as any).inspectionInterval.replace(/_/g, ' ')}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Dates & Expiration */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              Dates & Expiration
            </h3>
            <div className="grid md:grid-cols-2 gap-4">
              {asset.expirationDate && (
                <div>
                  <label className="text-sm text-gray-600">Expiration Date</label>
                  <p className="text-base font-medium text-gray-900">
                    {new Date(asset.expirationDate).toLocaleDateString()}
                  </p>
                  {asset.daysUntilExpiration !== null && (
                    <p className={`text-sm ${asset.daysUntilExpiration < 30 ? 'text-red-600' : 'text-gray-600'}`}>
                      {asset.daysUntilExpiration < 0 ? 'EXPIRED' : `${asset.daysUntilExpiration} days remaining`}
                    </p>
                  )}
                </div>
              )}
              {asset.calibrationDue && (
                <div>
                  <label className="text-sm text-gray-600">Calibration Due</label>
                  <p className="text-base font-medium text-gray-900">
                    {new Date(asset.calibrationDue).toLocaleDateString()}
                  </p>
                  {asset.daysUntilCalibration !== null && (
                    <p className={`text-sm ${asset.daysUntilCalibration < 30 ? 'text-red-600' : 'text-gray-600'}`}>
                      {asset.daysUntilCalibration < 0 ? 'OVERDUE' : `${asset.daysUntilCalibration} days remaining`}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Current Holder */}
          {asset.currentHolder && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <User className="h-5 w-5" />
                Current Holder
              </h3>
              <p className="text-base font-medium text-gray-900">
                {asset.currentHolder.firstName} {asset.currentHolder.lastName}
              </p>
            </div>
          )}

          {/* Last Inspection */}
          {asset.lastInspection && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Last Inspection
              </h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-gray-600">Date</label>
                  <p className="text-base font-medium text-gray-900">
                    {new Date(asset.lastInspection.date).toLocaleDateString()}
                  </p>
                </div>
                <div>
                  <label className="text-sm text-gray-600">Status</label>
                  <p className="text-base font-medium text-gray-900">{asset.lastInspection.status}</p>
                </div>
                <div className="md:col-span-2">
                  <label className="text-sm text-gray-600">Inspector</label>
                  <p className="text-base font-medium text-gray-900">{asset.lastInspection.inspector}</p>
                </div>
              </div>
            </div>
          )}
          </>
          ) : (
            historyLoading ? <p role="status">Loading saved history…</p> :
            historyError ? <p role="alert" className="text-red-700">{historyError}</p> :
            <AssetHistoryTimeline
              assetId={asset.id}
              assetName={asset.name}
              events={history}
            />
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50">
          {canManage && (
            <div className="flex gap-3">
              <button
                onClick={onDelete}
                className="px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg transition flex items-center gap-2"
              >
                <Trash2 className="h-4 w-4" />
                Delete Asset
              </button>
            </div>
          )}
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition"
            >
              Close
            </button>
            {canManage && (
              <button
                onClick={onEdit}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition flex items-center gap-2"
              >
                <Edit2 className="h-4 w-4" />
                Edit Asset
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

